import React, { useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  Box, Flex, Text, VStack, CircularProgress, CircularProgressLabel,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiXCircle, FiInfo, FiCheckCircle, FiShield } from 'react-icons/fi';
import { lintEmail } from './emailLint';

const SEV = {
  error: { color: '#ef4444', Icon: FiXCircle, label: 'Error' },
  warning: { color: '#f59e0b', Icon: FiAlertTriangle, label: 'Warning' },
  info: { color: '#2563eb', Icon: FiInfo, label: 'Tip' },
};

export default function EmailLintModal({ isOpen, onClose, blocks, html, settings, subject }) {
  const report = useMemo(
    () => (isOpen ? lintEmail(blocks || [], html || '', settings || {}, subject || '') : null),
    [isOpen, blocks, html, settings, subject]
  );
  if (!report) return <Modal isOpen={isOpen} onClose={onClose}><ModalOverlay /></Modal>;

  const tier = report.score >= 85
    ? { color: '#10b981', label: 'Looks great' }
    : report.score >= 60
      ? { color: '#f59e0b', label: 'A few things to fix' }
      : { color: '#ef4444', label: 'Needs attention before sending' };

  const order = { error: 0, warning: 1, info: 2 };
  const sorted = [...report.issues].sort((a, b) => order[a.severity] - order[b.severity]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="16px" overflow="hidden">
        <ModalHeader
          fontSize="15px" fontWeight="700" py={3} px={5}
          bg="gray.50" borderBottom="1px solid" borderBottomColor="gray.100"
        >
          <Flex align="center" gap={3}>
            <Box
              w={7} h={7} borderRadius="8px"
              style={{ background: `linear-gradient(135deg, ${tier.color} 0%, ${tier.color}cc 100%)` }}
              display="flex" alignItems="center" justifyContent="center"
              shadow={`0 2px 8px ${tier.color}55`}
            >
              <FiShield size={13} color="white" />
            </Box>
            <Text color="gray.700">Pre-send Check</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={2.5} right={4} />
        <ModalBody pb={5} pt={4}>
          <Box
            mb={4} p={4} borderRadius="12px"
            style={{
              background: `linear-gradient(135deg, ${tier.color}14 0%, ${tier.color}05 100%)`,
              border: `1px solid ${tier.color}2a`,
            }}
          >
            <Flex align="center" gap={4}>
              <CircularProgress value={report.score} color={tier.color} trackColor={`${tier.color}18`} size="64px" thickness="8px" capIsRound>
                <CircularProgressLabel fontSize="15px" fontWeight="800" color={tier.color}>{report.score}</CircularProgressLabel>
              </CircularProgress>
              <Box flex={1}>
                <Text fontSize="13.5px" fontWeight="800" color="gray.800">{tier.label}</Text>
                <Flex gap={2.5} mt={1.5} fontSize="11px" color="gray.500" fontWeight="600">
                  <Text>{report.sizeKB}KB</Text><Text color="gray.300">•</Text>
                  <Text>{report.links} links</Text><Text color="gray.300">•</Text>
                  <Text>{report.images} images</Text>
                </Flex>
              </Box>
            </Flex>
          </Box>

          {sorted.length === 0 ? (
            <Flex
              direction="column" align="center" py={9} gap={2.5} borderRadius="12px"
              style={{ background: 'linear-gradient(135deg, #10b98114 0%, #10b98105 100%)' }}
            >
              <Box
                w={10} h={10} borderRadius="full" bg="#10b981" display="flex"
                alignItems="center" justifyContent="center" shadow="0 4px 14px #10b98155"
              >
                <FiCheckCircle size={20} color="white" />
              </Box>
              <Text fontSize="13px" fontWeight="700" color="#059669">No issues found — good to send.</Text>
            </Flex>
          ) : (
            <VStack align="stretch" spacing={2}>
              {sorted.map((it, i) => {
                const s = SEV[it.severity] || SEV.info;
                return (
                  <Flex
                    key={i} gap={3} p={3} borderRadius="10px" align="flex-start"
                    bg="white" border="1px solid" borderColor="gray.100"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)', borderLeft: `3px solid ${s.color}` }}
                  >
                    <Box
                      flexShrink={0} w={6} h={6} borderRadius="7px" mt="1px"
                      display="flex" alignItems="center" justifyContent="center"
                      style={{ background: `${s.color}18` }}
                    >
                      <s.Icon size={13} color={s.color} />
                    </Box>
                    <Box flex={1}>
                      <Flex align="center" gap={2} mb="2px" flexWrap="wrap">
                        <Text fontSize="12.5px" fontWeight="700" color="gray.800">{it.message}</Text>
                        <Text
                          as="span" fontSize="9px" fontWeight="700" color="white" px={1.5} py="1px"
                          borderRadius="full" textTransform="uppercase" letterSpacing="0.02em"
                          style={{ background: s.color }}
                        >
                          {it.category}
                        </Text>
                      </Flex>
                      {it.fix && <Text fontSize="11px" color="gray.500">{it.fix}</Text>}
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
